import { useCallback, useEffect, useRef, useState } from 'react'
import { AmbientId, AmbientPrefs } from '../types/pomodoro'
import { KEYS, load, save } from '../utils/storage'

const FADE = 0.7
const LOOP_SECONDS = 8

/** Ambient beds are synthesized noise — nothing to download, no licensing, seamless loops. */
function buildBuffer(ctx: AudioContext, id: AmbientId): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * LOOP_SECONDS)
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate)

  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch)
    let lp = 0
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1
      if (id === 'rain') {
        lp += 0.45 * (white - lp)
        data[i] = (white * 0.55 + lp * 0.45) * 0.5
      } else if (id === 'ocean') {
        lp += 0.018 * (white - lp)
        const swell = 0.5 + 0.5 * Math.sin((2 * Math.PI * i * 2) / length)
        data[i] = lp * 7 * (0.35 + 0.65 * swell)
      } else {
        lp += 0.08 * (white - lp)
        data[i] = lp * 2.6
      }
    }

    if (id === 'forest') {
      // Sparse bird calls layered into the bed.
      for (let n = 0; n < 7; n++) {
        const startSec = 0.6 + n * 1.05 + (n % 3) * 0.12
        const start = Math.floor(startSec * ctx.sampleRate)
        const dur = Math.floor(0.14 * ctx.sampleRate)
        const base = 2100 + ((n * 431) % 1400)
        for (let i = 0; i < dur && start + i < length - 1; i++) {
          const t = i / dur
          const env = Math.sin(Math.PI * t) ** 2
          const freq = base * (1 + 0.25 * t)
          data[start + i] += Math.sin((2 * Math.PI * freq * i) / ctx.sampleRate) * env * 0.05
        }
      }
    }

    // Crossfade the tail into the head so the loop point is inaudible.
    const fade = Math.floor(0.05 * ctx.sampleRate)
    for (let i = 0; i < fade; i++) {
      const k = i / fade
      const tail = length - fade + i
      data[tail] = data[tail] * (1 - k) + data[i] * k
    }
    for (let i = 0; i < length; i++) {
      data[i] = Math.max(-1, Math.min(1, data[i]))
    }
  }

  return buffer
}

function filterFor(ctx: AudioContext, id: AmbientId): AudioNode[] {
  if (id === 'rain') {
    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 420
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 8200
    return [hp, lp]
  }
  if (id === 'ocean') {
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 520
    return [lp]
  }
  const hp = ctx.createBiquadFilter()
  hp.type = 'highpass'
  hp.frequency.value = 240
  const lp = ctx.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 4200
  return [hp, lp]
}

export interface AmbientAudio {
  sound: AmbientId | null
  volume: number
  playing: boolean
  error: string | null
  select: (id: AmbientId) => void
  toggle: () => void
  changeVolume: (value: number) => void
  pauseForSession: () => void
  resumeForSession: () => void
}

export function useAmbientAudio(): AmbientAudio {
  const stored = useRef<AmbientPrefs>(load<AmbientPrefs>(KEYS.ambient, { sound: null, volume: 0.5 }))
  const [sound, setSound] = useState<AmbientId | null>(stored.current.sound)
  const [volume, setVolume] = useState<number>(stored.current.volume)
  const [playing, setPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ctxRef = useRef<AudioContext | null>(null)
  const nodesRef = useRef<{ source: AudioBufferSourceNode; gain: GainNode } | null>(null)
  const buffersRef = useRef<Partial<Record<AmbientId, AudioBuffer>>>({})
  const wasPlayingRef = useRef(false)

  useEffect(() => {
    save(KEYS.ambient, { sound, volume })
  }, [sound, volume])

  const stop = useCallback(() => {
    const ctx = ctxRef.current
    const nodes = nodesRef.current
    nodesRef.current = null
    setPlaying(false)
    if (!ctx || !nodes) return
    try {
      const now = ctx.currentTime
      nodes.gain.gain.cancelScheduledValues(now)
      nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now)
      nodes.gain.gain.linearRampToValueAtTime(0.0001, now + FADE)
      nodes.source.stop(now + FADE + 0.05)
    } catch {
      /* already stopped */
    }
  }, [])

  const play = useCallback(
    (id: AmbientId) => {
      try {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        if (!Ctor) {
          setError('Audio is not supported in this browser.')
          return
        }
        if (!ctxRef.current) ctxRef.current = new Ctor()
        const ctx = ctxRef.current
        if (ctx.state === 'suspended') void ctx.resume()

        if (nodesRef.current) stop()

        if (!buffersRef.current[id]) buffersRef.current[id] = buildBuffer(ctx, id)
        const buffer = buffersRef.current[id]
        if (!buffer) throw new Error('buffer')

        const source = ctx.createBufferSource()
        source.buffer = buffer
        source.loop = true
        const gain = ctx.createGain()
        gain.gain.value = 0.0001

        const chain = filterFor(ctx, id)
        let node: AudioNode = source
        chain.forEach((f) => {
          node.connect(f)
          node = f
        })
        node.connect(gain)
        gain.connect(ctx.destination)

        const now = ctx.currentTime
        gain.gain.linearRampToValueAtTime(Math.max(0.0001, volume), now + FADE)
        source.start()

        nodesRef.current = { source, gain }
        setPlaying(true)
        setError(null)
      } catch {
        setError('Could not start ambient sound.')
        setPlaying(false)
      }
    },
    [stop, volume],
  )

  const select = useCallback(
    (id: AmbientId) => {
      setSound(id)
      play(id)
      wasPlayingRef.current = true
    },
    [play],
  )

  const toggle = useCallback(() => {
    if (playing) {
      wasPlayingRef.current = false
      stop()
      return
    }
    const id = sound ?? 'rain'
    setSound(id)
    play(id)
    wasPlayingRef.current = true
  }, [play, playing, sound, stop])

  const changeVolume = useCallback((value: number) => {
    setVolume(value)
    const ctx = ctxRef.current
    const nodes = nodesRef.current
    if (ctx && nodes) {
      const now = ctx.currentTime
      nodes.gain.gain.cancelScheduledValues(now)
      nodes.gain.gain.linearRampToValueAtTime(Math.max(0.0001, value), now + 0.15)
    }
  }, [])

  const pauseForSession = useCallback(() => {
    if (!playing) return
    wasPlayingRef.current = true
    stop()
  }, [playing, stop])

  const resumeForSession = useCallback(() => {
    if (playing || !wasPlayingRef.current || !sound) return
    play(sound)
  }, [play, playing, sound])

  useEffect(() => () => stop(), [stop])

  return { sound, volume, playing, error, select, toggle, changeVolume, pauseForSession, resumeForSession }
}
